<template>
  <section class="vesting-schedule-form">
    <div class="form-header">
      <div class="form-icon">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 5V10L13.3333 11.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h2 class="form-title">Default Vesting Information</h2>
    </div>
    
    <form @submit.prevent="checkFormValidation" class="form-content">
      <div class="form-row-double">
        <ComboField
          currentValue="Years"
          class="field-half"
        >
          <InputField
            class="combo-input"
            :validate="validateForm"
            label="Vesting period"
            type="number"
            :validateAsNumber="true"
            :currentValue="vesting.vesting_period"
            name="vesting_period"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
        </ComboField>
        <Dropdown
          class="field-half"
          name="vesting_frequency"
          :validate="validateForm"
          label="Vesting frequency"
          :currentValue="vesting.vesting_frequency"
          :options="vestingOptions"
          @input-changed="storeValue"
          @is-valid="handleValidation"
        />
      </div>
      
      <div class="form-row-double">
        <ComboField
          currentValue="Months"
          class="field-half"
        >
          <InputField
            class="combo-input"
            :validate="validateForm"
            label="Cliff period"
            type="number"
            :validateAsNumber="true"
            :currentValue="vesting.cliff_period"
            name="cliff_period"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
        </ComboField>
        <InputField
          v-if="marketPrice"
          class="field-half"
          :validate="validateForm"
          label="Strike price (USD)"
          type="text"
          :currentValue="strike_price"
          :validateAsNumber="true"
          :minValue="0"
          :maxValue="marketPrice"
          maxValueErrorMessage="Strike price can't be greater than market price"
          name="strike_price"
          @input-changed="storeValue"
          @is-valid="handleValidation"
        />
      </div>
      
      <div class="form-row-double">
        <ComboField currentValue="Months" class="field-half">
          <InputField
            class="combo-input"
            :validate="validateForm"
            label="Exercise timeline"
            :validateAsNumber="true"
            type="number"
            :currentValue="vesting.exercise_timeline"
            name="exercise_timeline"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
        </ComboField>
      </div>
      
      <div class="documents-section">
        <label class="section-label">Upload Stock options legal document</label>
        
        <div class="documents-list">
          <div
            class="document-item"
            v-for="(document, index) in documents"
            :key="index"
          >
            <InputField
              class="document-name-field"
              :validate="validateForm"
              label="Document name"
              type="text"
              name="name"
              :currentValue="document.name"
              @input-changed="storeValue($event, index)"
              @is-valid="handleValidation"
            />
            <DragNDrop
              accept=".pdf"
              :currentValue="document.url"
              :startUpload="uploadDocument"
              :documentName="document.name"
              :validate="documentValid"
              :hasMaxWidth="false"
              @re-upload="uploadDocument = false"
              @file-added="setDocument($event, index)"
              @upload-complete="setDocumentUrl($event, index)"
              @upload-error="uploadDocumentError"
            >
            </DragNDrop>
            <button
              v-if="documents.length > 1"
              type="button"
              class="remove-doc-btn"
              @click="removeDocument(document, index)"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 4H3.33333H14" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31304 1.33334 6.66667 1.33334H9.33333C9.68696 1.33334 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4H12.6667Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Remove</span>
            </button>
          </div>
        </div>
        
        <p class="documents-hint">You can upload multiple documents if they apply</p>
        
        <button
          type="button"
          class="add-doc-btn"
          @click="addDocument"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 3.33334V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3.33333 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Add more documents</span>
        </button>
      </div>
      
      <div class="form-footer">
        <CustomButton
          :title="buttonText"
          :loading="documentInfoLoading"
        />
      </div>
    </form>
  </section>
</template>

<script>
  import InputField from "@/components/general/InputField";
  import { convertToLocale } from "@/utils/helpers.js";
  import { mapActions, mapGetters, mapState } from "vuex";
  import CustomButton from "@/components/general/BtnComponent";
  import ComboField from "@/components/general/ComboField";
  import Dropdown from "@/components/general/Dropdown";
  import DragNDrop from "@/components/general/DragNDrop";
  import { getItem } from "@/utils/storage.js";

  export default {
    name: "VestingSchedule",
    data() {
      return {
        uploadDocument: false,
        documentInfoLoading: false,
        documentValid: false,
        currentToken: {},
        vesting: {
          vesting_period: null,
          vesting_frequency: "",
          exercise_timeline: null,
          cliff_period: null,
          strike_price: 0,
        },
        // document_url: "",
        documents: [{ type: "Esop", name: null, url: "" }],
        strike_price: "0", // default to 0
        validationResult: [],
        uploaded: [],
        validateForm: false,
        vestingOptions: [
          "Monthly",
          "Yearly",
          "Quarterly",
          "Daily",
          "Bi-annually",
        ],
      };
    },
    props: {
      buttonText: {
        type: String,
        required: true,
      },
      prefill: {
        type: Boolean,
        required: false,
        default: () => false,
      },
    },
    mounted() {
      if (this.prefill) {
        this.fillForm();
        this.strike_price = String(this.vestingSchedule?.strike_price);
      }
    },
    components: {
      ComboField,
      InputField,
      DragNDrop,
      CustomButton,
      Dropdown,
    },
    watch: {
      validationResult: {
        deep: true,
        handler(val) {
          if (
            val.length === Object.keys(this.vesting).length &&
            !val.includes(false)
          ) {
            this.documentInfoLoading = true;
            this.uploadDocument = true;
          }
        },
      },
      strikePriceFormatted(val) {
        this.strike_price = val;
      },
      prefill(val) {
        if (val) {
          this.fillForm();
        }
      },
    },
    computed: {
      ...mapGetters("Esop", ["vestingSchedule"]),
      ...mapState({
        orgDetails: (state) => state.organisationModule.organisationDetails,
      }),
      strikePriceFormatted() {
        let strikePriceFormatted = null;
        if (this.strike_price) {
          const strike_price = Number(this.strike_price.split(",").join(""));
          this.vesting.strike_price = strike_price;
          strikePriceFormatted = this.convertToLocale(strike_price);
        }
        return strikePriceFormatted;
      },

      marketPrice() {
        this.orgDetails.tokens?.map((token) => {
          if (token.entity === "ESO_TOKEN") {
            this.currentToken = token;
          }
        });
        return (
          Number(this.currentToken?.valuation) /
          Number(this.currentToken?.total_supply)
        );
      },
    },
    methods: {
      convertToLocale,
      ...mapActions("assetModule", ["deleteDocument"]),
      ...mapActions("Esop", ["createVestingSchedule", "updateVestingSchedule"]),
      ...mapActions(["showToast", "uploadMultipleDoc", "updateMultipleDoc"]),
      storeValue(object, index = null) {
        if (
          object.name !== "valuation" &&
          object.name !== "strike_price" &&
          object.name !== "name"
        ) {
          this.vesting[object.name] = object.value;
        } else if (object.name === "name") {
          // this[object.name] = object.value;
          this.documents[index][object.name] = object.value;
        } else {
          this[object.name] = object.value;
        }
      },
      fillForm() {
        if (this.vestingSchedule.documents?.length) {
          this.prefillDocuments(this.vestingSchedule.documents);
        }
        this.vesting.vesting_frequency = this.frequency(
          this.vestingSchedule.vesting_frequency
        );
        this.vesting.cliff_period = this.vestingSchedule?.cliff_period;
        this.strike_price = String(this.vestingSchedule?.strike_price);
        this.vesting.exercise_timeline =
          this.vestingSchedule?.exercise_timeline;
        this.vesting.vesting_period = this.vestingSchedule?.vesting_period;
        // this.documents = this.vestingSchedule?.documents;
      },
      prefillDocuments(documents) {
        let newDocuments = [];
        documents.map((document) => {
          const url = document.url;
          let obj = {
            type: document?.type,
            name: document?.name,
            url,
            _id: document?._id,
          };
          newDocuments.push(obj);
        });
        this.documents = newDocuments.reverse();
      },
      setDocument(val, index) {
        this.documents[index].url = val;
      },
      // setDocumentName(val, index) {
      //   console.log(this.documents[index][val.name]);
      //   this.documents[index][val.name] = val.value;
      // console.log(this.documents[index].name);
      // },
      addDocument() {
        this.documents.push({ type: "Esop", name: "", url: "" });
      },
      removeDocument(document, index) {
        this.documents.splice(index, 1);
        const assetId = this.$route.query?.asset;
        if (assetId && document.url.includes("cloudinary")) {
          const payload = {
            docId: document._id,
            assetId,
            type: "Esop",
          };
          this.deleteDocument(payload);
        }
      },
      checkFormValidation() {
        this.validationResult = [];
        this.validateForm = true;
        this.documentValid = true;
        setTimeout(() => {
          this.validateForm = false;
        }, 1000);
      },
      frequency(value) {
        let frequency = null;
        switch (value) {
          case "Daily":
            frequency = 1;
            break;
          case 1:
            frequency = "Daily";
            break;
          case 30:
            frequency = "Monthly";
            break;
          case "Monthly":
            frequency = 30;
            break;
          case 91:
            frequency = "Quarterly";
            break;
          case "Quarterly":
            frequency = 91;
            break;
          case 182:
            frequency = "Bi-annually";
            break;
          case "Bi-annually":
            frequency = 182;
            break;
          case 365:
            frequency = "Yearly";
            break;
          case "Yearly":
            frequency = 365;
            break;
        }
        return frequency;
      },
      setDocumentUrl(url, index) {
        this.documents[index].url = url;
        this.uploaded.push(true);
        if (this.uploaded.length === this.documents.length) {
          this.documentInfoLoading = false;
          this.createNewVestingSchedule();
        }
      },
      async createNewVestingSchedule() {
        const docPayload = {
          params: { doc_type: "Esop" },
          documents: { documents: this.documents },
        };
        const payload = {
          name: getItem("org_details").name,
          vesting_period: String(this.vesting.vesting_period),
          vesting_frequency: this.frequency(this.vesting.vesting_frequency),
          exercise_timeline: String(this.vesting.exercise_timeline),
          cliff_period: String(this.vesting.cliff_period),
          strike_price: String(this.vesting.strike_price),
        };
        let response = null;
        let message = "";
        try {
          switch (this.$route.name) {
            case "EsopDetails":
              response = await Promise.all([
                this.updateMultipleDoc(docPayload),
                this.updateVestingSchedule(payload),
              ]);
              message = response[1].data.message;
              break;
            case "CreateStockOptions":
              response = await Promise.all([
                this.uploadMultipleDoc(docPayload),
                this.createVestingSchedule(payload),
              ]);
              message = response[1].data.message;
              break;
          }
          // const response = await requestType;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
          this.documentInfoLoading = false;
          this.uploadDocument = false;
          this.$emit("next-page", "VestingSchedule");
        } catch (error) {
          return error;
        }
      },
      handleValidation(value) {
        this.validationResult.push(value);
      },
      uploadDocumentError() {
        this.documentInfoLoading = false;
        this.uploadTokenImage = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
.vesting-schedule-form {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 24px 20px;

  @media screen and (min-width: 768px) {
    padding: 32px;
  }
}

.form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
}

.form-icon {
  width: 36px;
  height: 36px;
  background: #EEF2FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4D84FF;
}

.form-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #0F172A;
  margin: 0;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row-double {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media screen and (min-width: 768px) {
    flex-direction: row;
    gap: 16px;
  }
}

.field-half {
  width: 100%;

  @media screen and (min-width: 768px) {
    width: 50%;
  }
}

.combo-input {
  flex-grow: 1;
  display: block;
  width: 66.67%;
}

.documents-section {
  margin-top: 12px;
}

.section-label {
  display: block;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
  margin-bottom: 16px;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.document-item {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 16px;
}

.document-name-field {
  margin-bottom: 12px;
}

.remove-doc-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 12px;
  background: none;
  border: none;
  color: #EF4444;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 6px;

  &:hover {
    background: #FEF2F2;
  }
}

.documents-hint {
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #64748B;
  margin: 16px 0;
}

.add-doc-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #F8FAFC;
  border: 1px dashed #CBD5E1;
  border-radius: 8px;
  color: #4D84FF;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #EEF2FF;
    border-color: #4D84FF;
  }
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
